import { EngineModule } from '@/types/engineWizard';
import { ModuleLifeLimit } from '@/data/moduleLifeLimits';

export interface TimeRemainingResult {
  hours: number;
  percentage: number;
  status: 'excellent' | 'good' | 'caution' | 'urgent' | 'overdue';
}

export function calculateTimeRemaining(
  tso: number | null,
  tbo: number | null
): TimeRemainingResult | null {
  if (tso === null || tbo === null || tbo === 0) {
    return null;
  }

  const remaining = tbo - tso;
  const percentage = ((tbo - tso) / tbo) * 100;

  let status: TimeRemainingResult['status'];
  if (percentage > 75) status = 'excellent';
  else if (percentage > 50) status = 'good';
  else if (percentage > 25) status = 'caution';
  else if (percentage > 0) status = 'urgent';
  else status = 'overdue';

  return {
    hours: Math.max(0, remaining),
    percentage: Math.max(0, Math.min(100, percentage)),
    status,
  };
}

export interface ModuleHealthResult {
  hoursRemaining: number | null;
  cyclesRemaining: number | null;
  overallHealth: number; // 0-100
  status: 'excellent' | 'good' | 'caution' | 'urgent' | 'overdue';
  limitingFactor: 'hours' | 'cycles' | 'condition' | 'none';
}

export function calculateModuleHealth(
  module: EngineModule,
  lifeLimit?: ModuleLifeLimit
): ModuleHealthResult {
  let hoursRemaining: number | null = null;
  let cyclesRemaining: number | null = null;
  let hoursPercentage = 100;
  let cyclesPercentage = 100;

  // Calculate hours remaining
  if (lifeLimit?.maxHours && module.tso !== null) {
    hoursRemaining = lifeLimit.maxHours - module.tso;
    hoursPercentage = (hoursRemaining / lifeLimit.maxHours) * 100;
  } else if (module.tso !== null && module.tsn !== null) {
    // Use TSN as fallback if no life limit specified
    hoursPercentage = module.tso > 0 ? ((module.tsn - module.tso) / module.tsn) * 100 : 100;
  }

  // Calculate cycles remaining
  if (lifeLimit?.maxCycles && module.cso !== null) {
    cyclesRemaining = lifeLimit.maxCycles - module.cso;
    cyclesPercentage = (cyclesRemaining / lifeLimit.maxCycles) * 100;
  } else if (module.cso !== null && module.csn !== null) {
    // Use CSN as fallback
    cyclesPercentage = module.cso > 0 ? ((module.csn - module.cso) / module.csn) * 100 : 100;
  }

  // Condition affects health
  let conditionScore = 100;
  switch (module.condition) {
    case 'new':
      conditionScore = 100;
      break;
    case 'overhauled':
      conditionScore = 95;
      break;
    case 'serviceable':
      conditionScore = 75;
      break;
    case 'needs_repair':
      conditionScore = 25;
      break;
    default:
      conditionScore = 50;
  }

  // Overall health is the minimum of hours%, cycles%, and condition
  const overallHealth = Math.min(hoursPercentage, cyclesPercentage, conditionScore);

  let status: ModuleHealthResult['status'];
  if (overallHealth > 75) status = 'excellent';
  else if (overallHealth > 50) status = 'good';
  else if (overallHealth > 25) status = 'caution';
  else if (overallHealth > 0) status = 'urgent';
  else status = 'overdue';

  // Determine limiting factor
  let limitingFactor: ModuleHealthResult['limitingFactor'] = 'none';
  if (conditionScore <= hoursPercentage && conditionScore <= cyclesPercentage) {
    limitingFactor = 'condition';
  } else if (hoursPercentage <= cyclesPercentage) {
    limitingFactor = 'hours';
  } else if (cyclesPercentage < hoursPercentage) {
    limitingFactor = 'cycles';
  }

  return {
    hoursRemaining,
    cyclesRemaining,
    overallHealth: Math.max(0, Math.min(100, overallHealth)),
    status,
    limitingFactor,
  };
}

export interface CostOfOwnershipResult {
  total: number;
  breakdown: {
    purchase: number;
    nearTermOverhaul: number;
    estimatedAnnualMaintenance: number;
  };
  yearsUntilOverhaul: number;
}

export function estimateTotalCostOfOwnership(
  purchasePrice: number,
  tso: number | null,
  tbo: number | null,
  typicalOverhaulCost: number,
  averageAnnualHours: number = 500
): CostOfOwnershipResult {
  const timeRemaining = calculateTimeRemaining(tso, tbo);
  const hoursUntilOverhaul = timeRemaining?.hours ?? tbo ?? 0;
  const yearsUntilOverhaul = hoursUntilOverhaul / averageAnnualHours;

  // Estimate annual maintenance at 5% of purchase price
  const estimatedAnnualMaintenance = purchasePrice * 0.05;

  // If overhaul is needed soon (< 500 hours), include it in near-term costs
  const nearTermOverhaul = hoursUntilOverhaul < 500 ? typicalOverhaulCost : 0;

  const total = purchasePrice + nearTermOverhaul + estimatedAnnualMaintenance;

  return {
    total,
    breakdown: {
      purchase: purchasePrice,
      nearTermOverhaul,
      estimatedAnnualMaintenance,
    },
    yearsUntilOverhaul,
  };
}

export function formatHours(hours: number): string {
  return hours.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function formatCycles(cycles: number): string {
  return cycles.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function getStatusColor(
  status: 'excellent' | 'good' | 'caution' | 'urgent' | 'overdue'
): string {
  switch (status) {
    case 'excellent':
      return 'text-green-600 dark:text-green-400';
    case 'good':
      return 'text-blue-600 dark:text-blue-400';
    case 'caution':
      return 'text-amber-600 dark:text-amber-400';
    case 'urgent':
      return 'text-orange-600 dark:text-orange-400';
    case 'overdue':
      return 'text-red-600 dark:text-red-400';
  }
}

export function getStatusBgColor(
  status: 'excellent' | 'good' | 'caution' | 'urgent' | 'overdue'
): string {
  switch (status) {
    case 'excellent':
      return 'bg-green-100 dark:bg-green-900/20';
    case 'good':
      return 'bg-blue-100 dark:bg-blue-900/20';
    case 'caution':
      return 'bg-amber-100 dark:bg-amber-900/20';
    case 'urgent':
      return 'bg-orange-100 dark:bg-orange-900/20';
    case 'overdue':
      return 'bg-red-100 dark:bg-red-900/20';
  }
}
